/**
 * Okendo renders its widgets once, on page load. The PDP swaps products
 * underneath it — cup-size and colour swatches fetch a different product
 * handle — but the reviews widget lives in its own `#okendo-app` section that
 * is not re-fetched, so it stays bound to whichever product it rendered with.
 *
 * `okeWidgetApi.setProduct` repoints a container at another product and forces
 * a re-render. `okeWidgetApi.initWidget` renders a container that has not
 * rendered yet and is a no-op for the rest, which is what freshly injected
 * markup (quickshop, section re-fetch) needs. Both hang off the async
 * `okendo-reviews.js` bundle, which announces itself with `oke-script-loaded`.
 *
 * @see https://docs.okendo.io/docs/on-site/storefront-javascript-api/widget-plus-window-api
 */

const widgetSelector = [
  '[data-oke-widget]',
  '[data-oke-star-rating]',
  '[data-oke-questions-widget]',
  '[data-oke-reviews-summary]',
  '[data-oke-reviews-keywords]',
  '[data-oke-reviews-modal-trigger]',
  '[data-oke-carousel]',
  '[data-oke-media-grid]',
  '[data-oke-media-carousel]',
].join(',');

const productIdAttribute = 'data-oke-reviews-product-id';

/**
 * Okendo namespaces product ids by platform (`shopify-7822023688294`).
 *
 * @param {string | number} productId
 * @returns {string}
 */
function toOkendoProductId(productId) {
  const id = String(productId);
  return id.startsWith('shopify-') ? id : `shopify-${id}`;
}

/**
 * Bring Okendo widgets back in line with the DOM after a dynamic update.
 *
 * @param {Object} [options]
 * @param {ParentNode} [options.scope=document] - Subtree to scan for widgets. Pass the
 *   quickshop modal rather than `document` so the PDP behind it keeps its own reviews.
 * @param {string | number} [options.productId] - Shopify product id now shown in `scope`.
 *   Omit when the markup was just re-fetched and already carries the right ids.
 */
export default function reinitOkendoWidgets({
  scope = document,
  productId,
} = {}) {
  if (!scope) {
    return;
  }

  const run = () => {
    const api = window.okeWidgetApi;
    if (!api) {
      return;
    }

    const nextProductId = productId ? toOkendoProductId(productId) : undefined;

    scope.querySelectorAll(widgetSelector).forEach(widget => {
      const currentProductId = widget.getAttribute(productIdAttribute);

      if (
        nextProductId &&
        currentProductId &&
        currentProductId !== nextProductId
      ) {
        api.setProduct(widget, nextProductId);
        return;
      }

      api.initWidget(widget);
    });
  };

  if (window.okeWidgetApi) {
    run();
  } else {
    document.addEventListener('oke-script-loaded', run, { once: true });
  }
}
